import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import type { Warehouse } from './types'

interface Props {
  warehouseId: string
  children: React.ReactNode
}

const TABS = [
  { id: 'loading-lists', label: 'Loading Lists',       seg: 'loading-lists' },
  { id: 'plans',         label: 'Pre-Devanning Plans', seg: 'plans' },
  { id: 'devannings',    label: 'Devannings',          seg: 'devannings' },
  { id: 'inbound',          label: 'Inbound',          seg: 'inbound' },
  { id: 'planned-outbound', label: 'Planned Outbound', seg: 'planned-outbound' },
  { id: 'deliveries',       label: 'Deliveries',       seg: 'deliveries' },
  { id: 'outbound',         label: 'Outbound',         seg: 'outbound' },
]
const STUBS = [
  { id: 'scan', label: 'Scan' },
]

export default function WarehouseLayout({ warehouseId, children }: Props) {
  const { pathname } = useLocation()
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)
  const [plannedCount, setPlannedCount] = useState<number | null>(null)
  const [notReadyCount, setNotReadyCount] = useState(0)

  useEffect(() => {
    supabase.from('warehouses').select('*').eq('id', warehouseId).single()
      .then(({ data }) => setWarehouse(data as Warehouse))
  }, [warehouseId])

  // Tab count badge -- "Outbound" deliberately stays uncounted (too much accumulated
  // history to be a useful number at a glance); Planned Outbound is a small, actionable queue.
  // Z50.X: also surfaces how many of those planned notes have goods that aren't actually in
  // the warehouse yet -- this is the module's own persistent reminder for that, deliberately
  // not a Dashboard banner (Dashboard/Customs stay module-cards-only; a status callout like
  // this belongs on the module it's about, per prior convention).
  useEffect(() => {
    // Merged single-note model: once a Collection + Delivery note is in transit
    // (transit_status = IN_TRANSIT) it's shown on the Deliveries tab instead, not counted here
    // -- same exclusion as PlannedOutbound's own query so the badge number always matches what
    // that tab actually lists.
    supabase.from('delivery_notes').select('id, readiness_status, devannings!inner(warehouse_id)')
      .eq('devannings.warehouse_id', warehouseId).eq('lifecycle_status', 'PLANNED').eq('status', 'ACTIVE')
      .or('transit_status.is.null,transit_status.eq.PENDING')
      .then(({ data }) => {
        const rows = (data as { readiness_status: string | null }[] | null) || []
        setPlannedCount(rows.length)
        setNotReadyCount(rows.filter(r => r.readiness_status && r.readiness_status !== 'READY').length)
      })
  }, [warehouseId, pathname])

  // Deliveries tab badge: in-transit count, so ops can see at a glance whether anything is
  // currently on the road for this warehouse without opening the tab.
  const [inTransitCount, setInTransitCount] = useState(0)
  useEffect(() => {
    supabase.from('delivery_notes').select('id, devannings!inner(warehouse_id)')
      .eq('devannings.warehouse_id', warehouseId)
      .eq('fulfilment_type', 'COLLECTION_AND_DELIVERY').eq('status', 'ACTIVE')
      .eq('lifecycle_status', 'PLANNED').eq('transit_status', 'IN_TRANSIT')
      .then(({ data }) => setInTransitCount((data || []).length))
  }, [warehouseId, pathname])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <Link to="/warehouse" className="text-sm text-blue-600 hover:underline mb-2 block">← Warehouses</Link>
        <div className="mb-6">
          {warehouse ? (
            <>
              <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">{warehouse.code}</span>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{warehouse.name}</h1>
            </>
          ) : (
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />
          )}
        </div>

        <div className="flex gap-1 mb-6 bg-white dark:bg-gray-800 rounded-xl p-1 shadow-sm border border-gray-200 dark:border-gray-700 w-fit flex-wrap">
          {TABS.map(t => {
            const isPlanned = t.id === 'planned-outbound'
            const isDeliveries = t.id === 'deliveries'
            const path = isPlanned && notReadyCount > 0 ? `/warehouse/${warehouseId}/${t.seg}?ready=not_ready` : `/warehouse/${warehouseId}/${t.seg}`
            const isActive = pathname.startsWith(`/warehouse/${warehouseId}/${t.seg}`)
            return (
              <Link key={t.id} to={path}
                title={isPlanned && notReadyCount > 0 ? `${notReadyCount} planned note(s) — goods not yet in warehouse`
                  : isDeliveries && inTransitCount > 0 ? `${inTransitCount} delivery note(s) currently in transit` : undefined}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}>
                {t.label}
                {isPlanned && plannedCount != null && plannedCount > 0 && ` (${plannedCount})`}
                {isPlanned && notReadyCount > 0 && (
                  <span className="ml-1 text-amber-500">⚠️{notReadyCount}</span>
                )}
                {isDeliveries && inTransitCount > 0 && ` (${inTransitCount})`}
              </Link>
            )
          })}
          {STUBS.map(t => (
            <span key={t.id} title="Coming soon — Phase 2"
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 dark:text-gray-600 cursor-not-allowed select-none">
              {t.label}
            </span>
          ))}
        </div>

        {children}
      </div>
    </div>
  )
}
